import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, LogIn, UserPlus, User, LogOut, LayoutDashboard, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { LoginModal } from '@/components/auth/LoginModal';
import { cn } from '@/lib/utils';

// Check if user is logged in
const getStoredUser = () => {
  try {
    const user = localStorage.getItem('sfretire_user');
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
};

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setUser(getStoredUser());
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem('sfretire_token');
    localStorage.removeItem('sfretire_user');
    setUser(null);
    navigate('/');
  };

  const getDashboardPath = () => {
    if (user?.role === 'admin') return '/admin';
    if (user?.role === 'entreprise' || user?.user_type === 'entreprise') {
      return '/dashboard/entreprise';
    }
    return '/dashboard/retraite';
  };

  const navLinks = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.enterprises'), href: '/entreprises' },
    { name: t('nav.retirees'), href: '/retraites' },
    { name: 'Missions', href: '/missions' },
    { name: 'Tarifs', href: '/tarifs' },
    { name: t('nav.howItWorks'), href: '/comment-ca-marche' },
    { name: 'Blog', href: '/blog' },
    { name: t('nav.contact'), href: '/contact' },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-background/95 backdrop-blur-md shadow-soft py-3'
          : 'bg-transparent py-5'
      )}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
              <rect width="40" height="40" rx="12" fill="#1e40af"/>
              <path d="M12.5 14.5C12.5 13.4 13.4 12.5 14.5 12.5H18.5C21.8 12.5 24 14.2 24 17C24 19 22.8 20.3 21 20.8L24.5 27.5H21.5L18.3 21.2H15.5V27.5H12.5V14.5ZM15.5 18.5H18.2C19.8 18.5 21 17.9 21 17C21 16.1 19.8 15.3 18.2 15.3H15.5V18.5Z" fill="white" opacity="0.3"/>
              <text x="10" y="28" fontFamily="Georgia, serif" fontWeight="bold" fontSize="18" fill="white">sf</text>
              <rect x="28" y="10" width="3" height="20" rx="1.5" fill="#f59e0b" opacity="0.9"/>
            </svg>
            <span className="font-serif text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
              sfretire<span className="text-primary">.com</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  'text-sm font-medium transition-colors hover:text-primary relative py-1',
                  location.pathname === link.href
                    ? 'text-primary'
                    : 'text-muted-foreground'
                )}
              >
                {link.name}
                {location.pathname === link.href && (
                  <motion.div
                    layoutId="navbar-indicator"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full"
                  />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Buttons & Language Switcher */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher variant="navbar" />
            {user ? (
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" asChild className="flex items-center gap-2">
                  <Link to={getDashboardPath()}>
                    <LayoutDashboard size={16} />
                    Dashboard
                  </Link>
                </Button>
                {(user.role === 'entreprise' || user.user_type === 'entreprise') && (
                  <Button variant="ghost" size="sm" asChild className="flex items-center gap-2">
                    <Link to="/recherche-experts" data-testid="nav-expert-search">
                      <Search size={16} />
                      Trouver un Expert
                    </Link>
                  </Button>
                )}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <User size={16} className="text-primary" />
                  )}
                  <span className="text-sm font-medium text-foreground">
                    {user.name || user.profile?.first_name || user.profile?.name || user.email?.split('@')[0]}
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={handleLogout} className="flex items-center gap-2">
                  <LogOut size={16} />
                  {t('nav.logout')}
                </Button>
              </div>
            ) : (
              <>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/retraites" className="flex items-center gap-2">
                    <UserPlus size={16} />
                    {t('nav.join')}
                  </Link>
                </Button>
                <Button variant="outline-gold" size="sm" onClick={() => setShowLoginModal(true)} className="flex items-center gap-2">
                  <LogIn size={16} />
                  {t('nav.login')}
                </Button>
              </>
            )}
            <Button variant="gold" size="sm" asChild>
              <Link to="/entreprises">{t('nav.exploreExperts')}</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageSwitcher variant="navbar" />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-foreground hover:text-primary transition-colors"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-background border-t border-border"
          >
            <div className="container mx-auto px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    'block py-2 text-base font-medium transition-colors',
                    location.pathname === link.href
                      ? 'text-primary'
                      : 'text-muted-foreground hover:text-primary'
                  )}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 flex flex-col gap-3">
                {user ? (
                  <>
                    <div className="flex items-center justify-center gap-2 py-2">
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <User size={20} className="text-primary" />
                      )}
                      <span className="font-medium text-foreground">
                        {user.name || user.profile?.first_name || user.profile?.name || user.email?.split('@')[0]}
                      </span>
                    </div>
                    <Button variant="gold" asChild onClick={() => setIsMobileMenuOpen(false)}>
                      <Link to={getDashboardPath()} className="flex items-center justify-center gap-2">
                        <LayoutDashboard size={16} />
                        Dashboard
                      </Link>
                    </Button>
                    {(user.role === 'entreprise' || user.user_type === 'entreprise') && (
                      <Button variant="outline-gold" asChild onClick={() => setIsMobileMenuOpen(false)}>
                        <Link to="/recherche-experts" className="flex items-center justify-center gap-2" data-testid="nav-mobile-expert-search">
                          <Search size={16} />
                          Trouver un Expert
                        </Link>
                      </Button>
                    )}
                    <Button variant="outline-gold" onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }} className="flex items-center justify-center gap-2">
                      <LogOut size={16} />
                      {t('nav.logout')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline-gold" asChild>
                      <Link to="/retraites" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center justify-center gap-2">
                        <UserPlus size={16} />
                        {t('nav.join')}
                      </Link>
                    </Button>
                    <Button variant="ghost" onClick={() => { setIsMobileMenuOpen(false); setShowLoginModal(true); }} className="flex items-center justify-center gap-2">
                      <LogIn size={16} />
                      {t('nav.login')}
                    </Button>
                  </>
                )}
                <Button variant="gold" asChild>
                  <Link to="/entreprises" onClick={() => setIsMobileMenuOpen(false)}>
                    {t('nav.exploreExperts')}
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        userType="retraite"
        onSwitchToRegister={() => {
          setShowLoginModal(false);
          window.location.href = '/retraites';
        }}
      />
    </motion.header>
  );
};
